import {
  DeleteOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Empty,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import axios from 'axios';
import { useContext, useEffect, useMemo, useState } from 'react';
import { BsBox2 } from 'react-icons/bs';
import { read as xlsxRead, utils as xlsxUtils } from 'xlsx';

import ProductImage from '../../components/productImage';
import {
  SPREADSHEET_ACCEPT,
  findEmptySheetError,
  findSpreadsheetFileError,
} from '../../utils/excelImport';
import { orderUnitRules } from '../../utils/formRules';
import SectionCard from '../../components/sectionCard/SectionCard';
import StepFooter from '../../components/stepFooter/StepFooter';
import { formatCurrency } from '../../utils/format';
import { AddOrderContext } from './AddOrderContext';
import ModalSelectProduct from './ModalSelectProduct';

const { confirm } = Modal;
const { Text } = Typography;

/** Барааны зурагны хэмжээ */
const IMAGE_SIZE = 40;

/** Барааны нэрийн хамгийн их өргөн (урт бол ellipsis + tooltip) */
const TITLE_MAX_WIDTH = 280;

/** Салбарын select-ийн өргөн — картын өргөнөөр сунахгүй, баруун талд зай үлдэнэ */
const SELECT_WIDTH = 260;

/** Excel-ээр бөөнөөр бараа хайх загварын файл */
const TEMPLATE_URL =
  'https://ppstorage-prod.s3.ap-southeast-1.amazonaws.com/mass_import_templates/Mass_product_find_templateASV.xlsx';

/** Тоог тооцоололд оруулахад NaN гарахаас хамгаална */
const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const Step1 = ({ nextStep }) => {
  const [form] = Form.useForm();

  /**
   * Сонгосон бараа, формын утгууд нь контекстэд байна — Step1 нь алхам солиход
   * unmount болдог бөгөөд контекст нь sessionStorage-д хадгалагддаг тул
   * "Буцах" дарахад ч, refresh хийхэд ч сонголт нь сэргэнэ.
   */
  const {
    setProducts,
    setBranchCode,
    setTypeMeat,
    typeMeat,
    setFinalProductList,
    setFormDataStep1,
    chosenProductList,
    setChosenProductList,
    formValuesStep1,
    setFormValuesStep1,
  } = useContext(AddOrderContext) ?? {};

  const [fields, setFields] = useState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [excelImporting, setExcelImporting] = useState(false);

  const branchLabel = 'Бараа татах салбар';

  useEffect(() => {
    setReferenceLoading(true);
    axios
      .get('/api/v1/request/procurement-reference')
      .then(({ data }) => setFields(data.result))
      .catch((e) => {
        console.error('procurement-reference: ', e);
      })
      .finally(() => setReferenceLoading(false));
  }, []);

  /**
   * Хүснэгтийн тоо ширхэгийн талбарууд нь мөрийн дугаараар нэрлэгддэг
   * (`products.warehousN.totalUnit`). Жагсаалт өөрчлөгдөх бүрд формын утгыг
   * дахин зэрэгцүүлнэ — эс бөгөөс мөр устгасны дараа доод мөрүүдийн тоо
   * ширхэг нэг мөр дээшээ гулсаж харагдана.
   */
  const syncUnitFields = (list) => {
    const products = {};
    list.forEach((item, index) => {
      products[`warehous${index}`] = { totalUnit: item?.totalUnit };
    });
    form.setFieldsValue({ products });
    // setFieldsValue нь onValuesChange-ыг дуудахгүй тул draft-ыг өөрсдөө шинэчилнэ
    setFormValuesStep1(form.getFieldsValue());
  };

  const updateChosenProducts = (list) => {
    setChosenProductList(list);
    syncUnitFields(list);
  };

  const getProductsData = async (searchData) => {
    const multiProductFetch = await axios
      .post('/api/v1.0/product/rawListBySkuBarcode', { searchData, isWarehouse: false })
      .then((response) => response.data)
      .catch((err) => {
        console.error('rawListBySkuBarcode: ', err);
        return null;
      });

    if (!multiProductFetch?.result) return;

    updateChosenProducts([...multiProductFetch.result]);
  };

  const onUploadExcel = async (eventFile) => {
    if (!eventFile) return true;

    // Excel биш файл чимээгүй уншигдаж байсныг зогсооно
    const fileError = findSpreadsheetFileError(eventFile);
    if (fileError) {
      message.error(fileError);
      return false;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const workbook = xlsxRead(e.target.result, { type: 'array', raw: true });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = xlsxUtils.sheet_to_json(worksheet, { raw: false });

      // Мөр байхгүй файл чимээгүй өнгөрдөг байсныг зогсооно
      const emptyError = findEmptySheetError(rows);
      if (emptyError) {
        message.warning(emptyError);
        return;
      }

      setExcelImporting(true);
      try {
        await getProductsData(rows);
      } finally {
        setExcelImporting(false);
      }
    };
    reader.readAsArrayBuffer(eventFile);
    return false;
  };

  const onClickSelectProduct = () => {
    if (!typeMeat) {
      message.error(`${branchLabel} сонгоно уу!`);
      return;
    }
    setIsModalVisible(true);
  };

  const onChangeProductUnit = (index, unit) => {
    setChosenProductList((prev) =>
      prev.map((item, i) => (i === index ? { ...item, totalUnit: unit } : item))
    );
  };

  const removeProduct = (record) =>
    confirm({
      title: 'Барааг устгах уу?',
      okText: 'Устгах',
      cancelText: 'Цуцлах',
      okButtonProps: { danger: true },
      onOk: () => updateChosenProducts(chosenProductList.filter((item) => item._id !== record._id)),
    });

  const totalPrice = useMemo(
    () => chosenProductList.reduce((sum, item) => sum + num(item?.price) * num(item?.totalUnit), 0),
    [chosenProductList]
  );

  const columns = [
    {
      key: 'index',
      title: '№',
      width: 48,
      align: 'center',
      render: (_, record, index) => <Text type="secondary">{index + 1}</Text>,
    },
    {
      key: 'product',
      title: 'Бараа',
      render: (_, record) => (
        <Space size={12} align="center">
          <ProductImage
            src={record?.imageCover}
            alt={record?.title ?? 'Барааны зураг'}
            size={IMAGE_SIZE}
            style={{
              objectFit: 'contain',
              borderRadius: 4,
              border: '1px solid #eaecf0',
              boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
            }}
          />
          <Space direction="vertical" size={0} style={{ maxWidth: TITLE_MAX_WIDTH }}>
            <Text style={{ maxWidth: TITLE_MAX_WIDTH }} ellipsis={{ tooltip: record?.title }}>
              {record?.title ?? '-'}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {record?.productSku}
            </Text>
          </Space>
        </Space>
      ),
    },
    {
      key: 'price',
      title: 'Нэгжийн үнэ',
      align: 'right',
      width: 140,
      render: (_, record) => formatCurrency(num(record?.price)),
    },
    {
      key: 'totalUnit',
      title: 'Тоо ширхэг',
      align: 'right',
      width: 160,
      /**
       * `initialValue` өгөхгүй: тоо ширхэг нь `syncUnitFields`-ээр (бараа
       * нэмэх/устгах бүрд) болон draft-аас сэргэсэн `formValuesStep1`-ээр
       * формын санд аль хэдийн бичигдсэн байдаг. Хоёуланг өгвөл antd
       * "Form already set initialValues" гэж анхааруулна.
       */
      render: (_, record, index) => (
        <Form.Item
          name={['products', `warehous${index}`, 'totalUnit']}
          style={{ marginBottom: 0 }}
          rules={orderUnitRules()}
        >
          {/* `min`/`max` тавихгүй — тэдгээр нь утгыг чимээгүй хумьдаг */}
          <InputNumber
            placeholder="0"
            addonAfter="ш"
            style={{ width: '100%' }}
            onChange={(value) => onChangeProductUnit(index, value)}
          />
        </Form.Item>
      ),
    },
    {
      key: 'total',
      title: 'Нийт үнэ',
      align: 'right',
      width: 150,
      render: (_, record) => (
        <Text strong>{formatCurrency(num(record?.price) * num(record?.totalUnit))}</Text>
      ),
    },
    {
      key: 'action',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Устгах">
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => removeProduct(record)}
          />
        </Tooltip>
      ),
    },
  ];

  /** Хүснэгтийн нийлбэр мөр — тоо ширхэг, нийт үнэ */
  const renderSummary = (rows = []) => {
    const totalUnit = rows.reduce((sum, row) => sum + num(row?.totalUnit), 0);
    const rowsPrice = rows.reduce((sum, row) => sum + num(row?.totalUnit) * num(row?.price), 0);

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell index={0} colSpan={3} align="right">
          <Text type="secondary">Нийт</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={3} align="right">
          <Text strong>{`${totalUnit} ш`}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={4} align="right">
          <Text strong>{formatCurrency(rowsPrice)}</Text>
        </Table.Summary.Cell>
        <Table.Summary.Cell index={5} />
      </Table.Summary.Row>
    );
  };

  const branchOptions = (fields?.branches?.types ?? []).map((value) => ({
    value,
    label: fields?.branches?.detail?.[value]?.name,
  }));

  const onFinish = (values) => {
    if (chosenProductList.length === 0) {
      message.error('Бараа сонгоогүй байна!');
      return;
    }

    setFormDataStep1({
      branchCode: values.positionGroup,
      products: chosenProductList,
      formValues: form.getFieldsValue(),
    });
    setProducts(
      chosenProductList.map((item) => ({
        productSKU: item.productSku,
        totalUnit: item.totalUnit,
      }))
    );
    setBranchCode(values.positionGroup);
    setFinalProductList(chosenProductList);
    nextStep();
  };

  return (
    <Form
      form={form}
      layout="vertical"
      autoComplete="off"
      onFinish={onFinish}
      initialValues={{ ...(formValuesStep1 ?? {}) }}
      /**
       * Refresh хийхэд сонгосон салбар, тоо ширхэг сэргэхийн тулд формын утгыг
       * контекст рүү шингээнэ (контекст нь sessionStorage-д хадгална).
       */
      onValuesChange={(_, allValues) => setFormValuesStep1(allValues)}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <SectionCard icon={<BsBox2 />} title="Барааны мэдээлэл">
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row gutter={[16, 8]} align="bottom">
              {/* Select нь дэлгэцийн хэмжээнээс үл хамааран богино — үлдсэн зай нь чөлөөтэй */}
              <Col flex={`0 0 ${SELECT_WIDTH}px`} style={{ maxWidth: '100%' }}>
                <Form.Item
                  label={branchLabel}
                  name="positionGroup"
                  rules={[{ required: true, message: `${branchLabel} сонгоно уу!` }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="Сонгох"
                    options={branchOptions}
                    loading={referenceLoading}
                    onChange={(value) => {
                      setTypeMeat(value);
                      // Салбар солиход өмнөх салбарын бараа хүчингүй болно
                      updateChosenProducts([]);
                    }}
                  />
                </Form.Item>
              </Col>

              <Col flex="auto">
                <Space size={8} wrap>
                  <Button icon={<DownloadOutlined />} href={TEMPLATE_URL}>
                    Excel загвар
                  </Button>
                  <Upload
                    beforeUpload={onUploadExcel}
                    showUploadList={false}
                    accept={SPREADSHEET_ACCEPT}
                  >
                    <Button icon={<FileExcelOutlined />} loading={excelImporting}>
                      Excel-ээр нэмэх
                    </Button>
                  </Upload>
                  <Button type="primary" icon={<PlusOutlined />} onClick={onClickSelectProduct}>
                    Бараа нэмэх
                  </Button>
                </Space>
              </Col>
            </Row>

            {chosenProductList.length ? (
              <Table
                columns={columns}
                dataSource={chosenProductList}
                rowKey={(record, index) => record?._id ?? index}
                size="middle"
                pagination={false}
                summary={renderSummary}
                scroll={{ x: 'max-content' }}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    {typeMeat
                      ? '"Бараа нэмэх" товчоор бараагаа сонгоно уу.'
                      : 'Эхлээд бараа татах салбараа сонгоно уу.'}
                  </Text>
                }
                style={{ margin: 0, padding: '24px 0' }}
              />
            )}
          </Space>
        </SectionCard>

        <StepFooter
          extra={
            totalPrice > 0 ? (
              <Text type="secondary">
                Барааны нийт дүн: <Text strong>{formatCurrency(totalPrice)}</Text>
              </Text>
            ) : null
          }
        />
      </Space>

      {isModalVisible && (
        <ModalSelectProduct
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          selectedProducts={chosenProductList}
          onSelect={updateChosenProducts}
        />
      )}
    </Form>
  );
};

export default Step1;
